import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { SectionCard } from '@/components/common/SectionCard';
import { isViewAsTab } from '@/lib/auth/session';
import { ROUTES } from '@/lib/constants/routes';
import type { BillingOverview, SubscriptionStatus } from '@/lib/types/billing';
import { formatDate } from '@/lib/utils/date.utils';
import { formatCents } from '@/lib/utils/format.utils';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { useUpcomingInvoice } from '../hooks/useUpcomingInvoice';
import { PlanBenefits } from './PlanBenefits';
import { INTERVAL_SUFFIX } from './PlanSummaryCard';

const COPY = BILLING_STRINGS.OVERVIEW;
const STATUS = BILLING_STRINGS.STATUS_LABELS;

/** The one status in which a subscription is over, not merely cancelling. */
const ENDED_STATUS = 'cancelled';

/** How each status reads at a glance, resolved once. */
const STATUS_BADGE: Record<SubscriptionStatus, BadgeVariant> = {
  active: 'success',
  grace_period: 'warning',
  past_due: 'danger',
  cancelled: 'neutral',
};

/** The banner's tone per situation. */
type NoticeTone = 'warning' | 'info' | 'ended';
const NOTICE_CLASSES: Record<NoticeTone, string> = {
  warning: 'border-yellow/40 bg-yellow/10',
  info: 'border-border-strong bg-surface-raised',
  ended: 'border-red/30 bg-red/5',
};

export interface CurrentPlanCardProps {
  overview: BillingOverview;
  /**
   * Seats the startup's access carries (FR-138), from the data-room access
   * view: the billing overview no longer reports seats, since none are bought.
   */
  includedSeats: number;
  onAutoRenewChange: (next: boolean) => void;
  onCancel: () => void;
  onManagePayment: () => void;
  isOpeningPortal: boolean;
}

/** One labelled fact in the plan card, as the design lays them out. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <dt className="text-text-muted w-36 shrink-0 text-xs font-medium tracking-wide uppercase">{label}</dt>
      <dd className="text-text-primary text-sm">{children}</dd>
    </div>
  );
}

/**
 * The current plan on Billing & Info (FR-41, MYN-113; 2026-09-22 design).
 *
 * Left: the plan, its status, price and the dates that matter, with the
 * auto-renewal switch. Right: what the plan includes and the way out. A banner
 * explains any state a member could misread - a failed payment, a scheduled
 * cancellation, or ended access - with the date it turns on.
 *
 * Nothing here is derived: whether cancellation and renewal are offered comes
 * from `canCancel` / `canManageAutoRenew`, because a one-time plan has nothing
 * to renew (A-78) and that rule lives in the backend.
 */
export function CurrentPlanCard({
  overview,
  includedSeats,
  onAutoRenewChange,
  onCancel,
  onManagePayment,
  isOpeningPortal,
}: CurrentPlanCardProps) {
  const hasEnded = overview.status === ENDED_STATUS;
  const autoRenewOn = !overview.cancelledAt;
  const upcoming = useUpcomingInvoice(overview.canManageAutoRenew && autoRenewOn && !hasEnded);
  const periodEnd = formatDate(overview.currentPeriodEnd);
  const notice: { tone: NoticeTone; text: string } | null = hasEnded
    ? { tone: 'ended', text: COPY.ENDED_NOTICE.replace('{date}', periodEnd) }
    : overview.gracePeriodEndsAt
      ? { tone: 'warning', text: COPY.GRACE_NOTICE.replace('{date}', formatDate(overview.gracePeriodEndsAt)) }
      : overview.cancelledAt
        ? { tone: 'info', text: COPY.CANCELLED_NOTICE.replace('{date}', periodEnd) }
        : null;
  // "Next billing date" only while something will actually bill on it.
  const dateLabel = hasEnded
    ? COPY.ACCESS_ENDED_LABEL
    : overview.canManageAutoRenew && autoRenewOn
      ? COPY.NEXT_BILLING_LABEL
      : COPY.ACCESS_ENDS_LABEL;

  return (
    <SectionCard title={COPY.PLAN_SECTION}>
      {notice && (
        <div role="status" className={`rounded-card mb-6 border p-4 ${NOTICE_CLASSES[notice.tone]}`}>
          <p className="text-text-primary text-sm">{notice.text}</p>
          {hasEnded && (
            <Button asChild className="mt-3 inline-flex">
              <Link to={ROUTES.BILLING_PURCHASE}>{COPY.SUBSCRIBE_AGAIN}</Link>
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-8 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:gap-0">
        <div className="flex flex-col gap-5 md:pr-8">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-text-primary text-xl font-semibold">{overview.planName}</p>
              <Badge variant={STATUS_BADGE[overview.status]} size="sm">
                {STATUS[overview.status]}
              </Badge>
            </div>
            <p className="text-text-secondary mt-1 text-sm">
              {`${formatCents(overview.planPriceCents) ?? ''}${INTERVAL_SUFFIX[overview.billingInterval]} · ${COPY.BILLED[overview.billingInterval]}`}
            </p>
          </div>

          <dl className="flex flex-col gap-3">
            <Fact label={dateLabel}>{periodEnd}</Fact>
            <Fact label={COPY.MEMBER_SINCE_LABEL}>{formatDate(overview.memberSince)}</Fact>
            {/* Only while a renewal is actually due: with auto-renewal off or
                the plan ended, nothing will be billed (2026-10-01). */}
            {upcoming.data && (
              <Fact label={COPY.NEXT_BILL_LABEL}>
                <span className="flex flex-col">
                  <span className="font-semibold">
                    {COPY.NEXT_BILL_VALUE.replace('{amount}', formatCents(upcoming.data.amountDueCents) ?? '').replace(
                      '{date}',
                      formatDate(upcoming.data.billingDate),
                    )}
                  </span>
                  {upcoming.data.creditCents > 0 && (
                    <span className="text-text-muted text-xs">
                      {COPY.NEXT_BILL_CREDIT.replace('{credit}', formatCents(upcoming.data.creditCents) ?? '')}
                    </span>
                  )}
                  <span className="text-text-muted text-xs">{COPY.NEXT_BILL_HELP}</span>
                </span>
              </Fact>
            )}
            {/* Absent, not disabled, on a one-time plan: there is nothing to
                renew, so a greyed toggle would only invite a hunt for how to
                enable it. */}
            {overview.canManageAutoRenew && (
              <Fact label={COPY.AUTO_RENEW_LABEL}>
                <span className="flex items-center gap-3">
                  <Switch
                    checked={autoRenewOn}
                    onCheckedChange={onAutoRenewChange}
                    label={COPY.AUTO_RENEW_LABEL}
                  />
                  {autoRenewOn ? COPY.AUTO_RENEW_ENABLED : COPY.AUTO_RENEW_DISABLED}
                </span>
              </Fact>
            )}
          </dl>

          {/* Never in a view-as tab: the Stripe portal shows the card on file (FR-242). */}
          {overview.canManagePaymentMethod && !isViewAsTab() && (
            <div className="flex flex-col gap-1">
              <Button variant="link" className="w-fit" onClick={onManagePayment} disabled={isOpeningPortal}>
                {COPY.MANAGE_PAYMENT}
              </Button>
              <p className="text-text-muted text-xs">{COPY.MANAGE_PAYMENT_HELP}</p>
            </div>
          )}
        </div>

        <div className="border-border-subtle flex flex-col gap-5 md:border-l md:pl-8">
          <p className="text-text-primary text-sm font-semibold">{COPY.INCLUDED_TITLE}</p>
          <PlanBenefits includedSeats={includedSeats} />
          {overview.canCancel && !overview.cancelledAt && (
            <Button variant="linkDanger" className="w-fit text-sm" onClick={onCancel}>
              {COPY.CANCEL_LINK}
            </Button>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
